import type { VideoModelConfig } from '../../types';

/** Wan2.7（独占 + 多模态参考：音视频同步、视频续写） */
export const wan2_7: VideoModelConfig = {
  id: '01KYPMN0SJMX79NH2SV9RE2CCF',
  displayName: 'Wan2.7',
  defaults: {
    ratio: '9:16',
    resolution: '720P',
    duration: 5,
  },
  ratioOptions: [
    { label: '1:1', value: '1:1' },
    { label: '3:4', value: '3:4' },
    { label: '4:3', value: '4:3' },
    { label: '9:16', value: '9:16' },
    { label: '16:9', value: '16:9' },
  ],
  resolutionOptions: [
    { label: '720P', value: '720P' },
    { label: '1080P', value: '1080P' },
  ],
  duration: { mode: 'range', min: 2, max: 15, step: 1, default: 5 },
  capabilities: {
    refVideo: true,
    refAudio: true,
    multiModalReference: true,
    multiVideo: true,
  },
  limits: {
    maxRefImages: 5,
    maxRefVideos: 1,
    maxRefAudios: 1,
    /**
     * 图片+视频共享总数上限（Wan2.7 特有：参考图 + 参考视频 总数 ≤ 5）。
     * ⚠️ 易混淆点：这是「总量约束」，覆盖 maxRefImages / maxRefVideos 的独立限制——
     * 该模型视频选择器允许最多 5 个（视频+图片共享），提交时校验总数。
     */
    refPoolMax: 5,
    // 参考视频上传约束（转写自 VideoSelectModal 原硬编码）：格式 mp4/mov、单文件 ≤100MB、时长 [1,30]s
    refVideoConstraints: {
      formats: ['mp4', 'mov'],
      maxFileSizeMB: 100,
      duration: { min: 1, max: 30 },
      hintKey: 'imageCreation.videoUploadHintWan27',
    },
    // 参考音频上传约束（转写自 AudioSelectModal 原硬编码）：格式 wav/mp3、单文件 ≤15MB、时长 [1,10]s
    refAudioConstraints: {
      formats: ['wav', 'mp3'],
      maxFileSizeMB: 15,
      duration: { min: 1, max: 10 },
      hintKey: 'imageCreation.audioUploadHintWan27',
    },
    // 提交前参考图约束（汇总层校验）：jpg/jpeg/png、宽高 [240,8000]、宽高比 [0.125,8]、单张 ≤20MB
    // ⚠️ 与 refImageConstraints（上传时）是「两层独立校验」；宽高/宽高比/大小仅在提交前汇总时校验
    refImageSubmitConstraints: {
      formats: ['jpg', 'jpeg', 'png'],
      maxFileSizeMB: 20,
      dimension: { min: 240, max: 8000 },
      aspectRatio: { min: 0.125, max: 8 },
      errorKeys: {
        size: 'imageCreation.wan27ImageSingleSizeExceeded',
        width: 'imageCreation.wan27ImageWidthOutOfRange',
        height: 'imageCreation.wan27ImageHeightOutOfRange',
        aspectRatio: 'imageCreation.wan27ImageAspectRatioOutOfRange',
      },
    },
    // 提交前参考视频约束（汇总层校验）：单文件 ≤100MB、宽高 [240,4096]、像素 [240²,4096²]、帧率 [10,60]、单段 1~30s
    // ⚠️ 原逻辑提交前不校验视频格式（仅上传时校验），故此处不声明 formats；与 refVideoConstraints（上传时）两层独立
    refVideoSubmitConstraints: {
      maxFileSizeMB: 100,
      dimension: { min: 240, max: 4096 },
      pixelCount: { min: 240 * 240, max: 4096 * 4096 },
      fps: { min: 10, max: 60 },
      duration: { min: 1, max: 30 },
      errorKeys: {
        size: 'imageCreation.wan27VideoSizeExceeded',
        width: 'imageCreation.wan27VideoWidthOutOfRange',
        height: 'imageCreation.wan27VideoHeightOutOfRange',
        pixel: 'imageCreation.wan27VideoPixelsOutOfRange',
        fps: 'imageCreation.wan27VideoFpsOutOfRange',
        duration: 'imageCreation.wan27VideoDurationOutOfRange',
      },
    },
    // 提交前参考音频约束（汇总层校验）：wav/mp3、单文件 ≤15MB、单段 1~10s、合计 ≤10s
    // ⚠️ 与 refAudioConstraints（上传时）是「两层独立校验」；单段/合计时长仅在提交前汇总时校验
    refAudioSubmitConstraints: {
      formats: ['wav', 'mp3'],
      maxFileSizeMB: 15,
      duration: { min: 1, max: 10, totalMax: 10 },
      errorKeys: {
        format: 'imageCreation.wan27AudioFormatNotSupported',
        size: 'imageCreation.wan27AudioSizeExceeded',
        durationMin: 'imageCreation.wan27AudioDurationOutOfRange',
        durationMax: 'imageCreation.wan27AudioDurationOutOfRange',
        totalDuration: 'imageCreation.wan27AudioTotalDurationExceeded',
      },
    },
  },
  /** 画布扩展（节点式业务专属字段，从画布 configs/wan-2.7.ts 迁移） */
  canvas: {
    hideRatioByTab: ['首尾帧'],
    durationExt: {
      dynamicMax: { withRefVideo: 10, withoutRefVideo: 15 },
    },
    capabilities: {
      refVideo: true,
      refAudio: true,
      atMentionTabs: ['全能参考', '图生视频', '视频编辑', '视频延长'],
    },
    limits: {
      validateRefImageSize: true,
      promptMaxLength: 5000,
      maxRefImages: 5,
      maxRefVideos: 5,
      maxRefAudios: 1,
      maxRefImagesWithRefVideo: 4,
      minRefMedia: 1,
      maxRefMediaTotal: 5,
      refImageConstraints: {
        formats: ['jpg', 'jpeg', 'png'],
        maxFileSizeMB: 20,
        dimension: { min: 240, max: 8000 },
        aspectRatio: { min: 0.125, max: 8 },
      },
      refVideoConstraints: {
        formats: ['mp4', 'mov'],
        maxFileSizeMB: 100,
        duration: { min: 2, max: 30, totalMax: 30 },
        dimension: { min: 240, max: 4096 },
        aspectRatio: { min: 0.125, max: 8 },
      },
      refAudioConstraints: {
        formats: ['wav', 'mp3'],
        maxFileSizeMB: 15,
        duration: { min: 1, max: 10, totalMax: 10 },
      },
    },
  },
};
